import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { useMapStore } from '../../store/useMapStore';

interface GoogleMapProps {
  className?: string;
}

const GoogleMap: React.FC<GoogleMapProps> = ({ className = 'h-96 w-full' }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [markers, setMarkers] = useState<google.maps.marker.AdvancedMarkerElement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const isInitializedRef = useRef(false);

  const { mapData, setCenter, setZoom, setActiveMarker, setSelectedLocation, updateActiveMarkerPosition } = useMapStore();

  // Memoize functions to prevent unnecessary re-renders
  const memoizedSetActiveMarker = useCallback(setActiveMarker, [setActiveMarker]);
  const memoizedSetCenter = useCallback(setCenter, [setCenter]);
  const memoizedSetZoom = useCallback(setZoom, [setZoom]);
  const memoizedSetSelectedLocation = useCallback(setSelectedLocation, [setSelectedLocation]);
  const memoizedUpdateActiveMarkerPosition = useCallback(updateActiveMarkerPosition, [updateActiveMarkerPosition]);

  // Load Google Maps API
  useEffect(() => {
    const loadGoogleMaps = async () => {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      
      if (!apiKey || apiKey === 'your_google_maps_api_key_here') {
        setError('Google Maps API key is missing or invalid. Please check your .env file.');
        setIsLoading(false);
        return;
      }

      const loader = new Loader({
        apiKey: apiKey,
        version: 'weekly',
        libraries: ['places', 'marker'],
      });

      try {
        console.log('Loading Google Maps...');
        await loader.load();
        console.log('Google Maps loaded successfully');
        setIsGoogleLoaded(true);
      } catch (err) {
        console.error('Error loading Google Maps:', err);
        setError(`Failed to load Google Maps: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setIsLoading(false);
      }
    };

    loadGoogleMaps();
  }, []);

  // Initialize map after Google Maps is loaded and DOM element exists
  useEffect(() => {
    console.log('Map init effect triggered:', { isGoogleLoaded, hasMapRef: !!mapRef.current, hasMap: !!map, isInitialized: isInitializedRef.current });
    
    if (!isGoogleLoaded) {
      console.log('Google Maps not loaded yet');
      return;
    }
    
    if (!mapRef.current) {
      console.log('Map ref not available yet');
      return;
    }
    
    if (map || isInitializedRef.current) {
      console.log('Map already exists or initialized');
      return;
    }

    const initMap = async () => {
      try {
        console.log('Initializing map...');
        const google = (window as any).google;
        
        if (!google) {
          console.error('Google object not found in window');
          setError('Google Maps not available');
          setIsLoading(false);
          return;
        }

        console.log('Creating map instance...');
        const mapInstance = new google.maps.Map(mapRef.current, {
          center: { lat: mapData.center.lat, lng: mapData.center.lng },
          zoom: mapData.zoom,
          mapTypeId: google.maps.MapTypeId.ROADMAP,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: false,
          scaleControl: false,
          rotateControl: false,
          tilt: 0,
          mapId: process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID',
        });

        setMap(mapInstance);
        isInitializedRef.current = true;
        console.log('Map instance created successfully');

        // Add click listener to map
        mapInstance.addListener('click', async (event: google.maps.MapMouseEvent) => {
          if (event.latLng) {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();
            
            // Get address using reverse geocoding
            const geocoder = new google.maps.Geocoder();
            try {
              const result = await geocoder.geocode({ location: { lat, lng } });
              const address = result.results[0]?.formatted_address || '';
              
              const location = {
                lat,
                lng,
                address,
              };
              memoizedSetActiveMarker(location);
            } catch (error) {
              console.error('Error getting address:', error);
              // Add marker without address if geocoding fails
              const location = {
                lat,
                lng,
              };
              memoizedSetActiveMarker(location);
            }
          }
        });

        // Add center change listener
        mapInstance.addListener('center_changed', () => {
          const center = mapInstance.getCenter();
          if (center) {
            memoizedSetCenter({ lat: center.lat(), lng: center.lng() });
          }
        });

        // Add zoom change listener
        mapInstance.addListener('zoom_changed', () => {
          memoizedSetZoom(mapInstance.getZoom() || 14);
        });

        setIsLoading(false);
        console.log('Map initialization complete');
      } catch (err) {
        console.error('Error initializing map:', err);
        setError(`Failed to initialize map: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setIsLoading(false);
      }
    };

    initMap();
  }, [isGoogleLoaded, memoizedSetActiveMarker, memoizedSetCenter, memoizedSetZoom, mapData.center.lat, mapData.center.lng, mapData.zoom]);

  // Update markers when mapData changes
  useEffect(() => {
    if (!map) return;

    // Clear existing markers
    markers.forEach(marker => marker.map = null);
    const newMarkers: google.maps.marker.AdvancedMarkerElement[] = [];

    // Add active marker (blue color)
    if (mapData.activeMarker) {
      const google = (window as any).google;
      
      // Create a blue pin element for the active marker
      const pinElement = document.createElement('div');
      pinElement.className = 'custom-marker active-marker';
      pinElement.innerHTML = `
        <div style="
          width: 24px;
          height: 24px;
          background-color: #3B82F6;
          border: 2px solid white;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          position: relative;
          cursor: grab;
        ">
          <div style="
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(45deg);
            color: white;
            font-size: 12px;
            font-weight: bold;
          ">A</div>
        </div>
      `;

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: mapData.activeMarker.lat, lng: mapData.activeMarker.lng },
        map: map,
        title: 'Active Marker',
        content: pinElement,
        gmpDraggable: true, // Make active marker draggable
      });

      // Add click listener to active marker
      marker.addListener('click', () => {
        memoizedSetSelectedLocation(mapData.activeMarker);
      });

      // Add drag end listener to update active marker position
      marker.addListener('dragend', async (event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
          const newLat = event.latLng.lat();
          const newLng = event.latLng.lng();
          
          // Get new address using reverse geocoding
          const geocoder = new google.maps.Geocoder();
          try {
            const result = await geocoder.geocode({ location: { lat: newLat, lng: newLng } });
            const newAddress = result.results[0]?.formatted_address || '';
            
            const newLocation = {
              lat: newLat,
              lng: newLng,
              address: newAddress,
            };
            
            memoizedUpdateActiveMarkerPosition(newLocation);
            console.log('Active marker moved to:', newLocation);
          } catch (error) {
            console.error('Error getting new address after drag:', error);
            // Update position without address if geocoding fails
            const newLocation = {
              lat: newLat,
              lng: newLng,
            };
            memoizedUpdateActiveMarkerPosition(newLocation);
          }
        }
      });

      newMarkers.push(marker);
    }

    // Add saved markers (green color)
    mapData.savedMarkers.forEach((savedMarker, index) => {
      const google = (window as any).google;
      
      // Create a green pin element for saved markers
      const pinElement = document.createElement('div');
      pinElement.className = 'custom-marker saved-marker';
      pinElement.innerHTML = `
        <div style="
          width: 24px;
          height: 24px;
          background-color: #10B981;
          border: 2px solid white;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          position: relative;
        ">
          <div style="
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(45deg);
            color: white;
            font-size: 12px;
            font-weight: bold;
          ">${index + 1}</div>
        </div>
      `;

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: savedMarker.lat, lng: savedMarker.lng },
        map: map,
        title: `Saved Marker ${index + 1}`,
        content: pinElement,
        gmpDraggable: false, // Saved markers are not draggable
      });

      // Add click listener to saved marker
      marker.addListener('click', () => {
        memoizedSetSelectedLocation(savedMarker);
      });

      newMarkers.push(marker);
    });

    setMarkers(newMarkers);
  }, [mapData.activeMarker, mapData.savedMarkers, map, memoizedSetSelectedLocation, memoizedUpdateActiveMarkerPosition]);

  // Update map center and zoom when mapData changes (only if map exists and not from user interaction)
  useEffect(() => {
    if (!map) return;

    // Only update if the change didn't come from the map itself
    const currentCenter = map.getCenter();
    const currentZoom = map.getZoom();
    
    if (currentCenter && 
        (Math.abs(currentCenter.lat() - mapData.center.lat) > 0.000001 || 
         Math.abs(currentCenter.lng() - mapData.center.lng) > 0.000001)) {
      map.setCenter({ lat: mapData.center.lat, lng: mapData.center.lng });
    }
    
    if (currentZoom !== mapData.zoom) {
      map.setZoom(mapData.zoom);
    }
  }, [mapData.center.lat, mapData.center.lng, mapData.zoom, map]);

  return (
    <div className="relative">
      {/* Always render the map container */}
      <div ref={mapRef} className={className} />
      
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-75 rounded-lg">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <div className="text-gray-600">Loading map...</div>
            <div className="text-xs text-gray-500 mt-2">
              Google loaded: {isGoogleLoaded ? 'Yes' : 'No'} | 
              Map ref: {mapRef.current ? 'Yes' : 'No'} | 
              Map instance: {map ? 'Yes' : 'No'}
            </div>
          </div>
        </div>
      )}
      
      {/* Error overlay */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-75 rounded-lg">
          <div className="text-center">
            <div className="text-red-600 text-lg font-semibold mb-2">Error Loading Map</div>
            <div className="text-gray-600">{error}</div>
            <div className="text-sm text-gray-500 mt-2">
              Please check your Google Maps API key in the .env file
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleMap; 